'use client';

import { ErrorInfo, useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useMediaQuery } from 'react-responsive';
import { useSearchParams } from 'next/navigation';
import Config from '@config';
import Button from '@atoms/Button/Button';
import Text from '@atoms/Text/Text';
import Heading from '@atoms/Heading/Heading';
import Link from '@atoms/Link/Link';
import Breadcrumbs from '@molecules/Breadcrumbs/Breadcrumbs';
import ProductQuantityControl from '@molecules/ProductQuantityControl/ProductQuantityControl';
import Modal, { ModalProps } from '@organisms/Modal/Modal';
import ErrorBoundary from '@utility/ErrorBoundary/ErrorBoundary';
import logger from '@logger';
import { StyledEnergyDeclarationImage } from '@molecules/ProductTableOfOther/ProductTableOfOther.styles';
import { isOfflineProduct, isOfflinePromotion, isOnlineProduct } from '@helpers/typeGuards/product';
import ProductDetailsTabs from './ProductDetailsTabs/ProductDetailsTabs';
import ProductDetailsPriceAndPromotionDetails from './ProductDetailsPriceAndPromotionDetails/ProductDetailsPriceAndPromotionDetails';
import ProductDetailsAccordion from './ProductDetailsAccordion/ProductDetailsAccordion';
import ProductDetailsRelated from './ProductDetailsRelated/ProductDetailsRelated';
import {
  BreadcrumbsWrapper,
  StyledNotFoundText,
  StyledProductDetails,
  StyledProductDetailsEnergyDeclaration,
  StyledProductDetailsManufacturerWrapper,
  StyledProductDetailsPriceAndPromotion,
  StyledProductDetailsQuantityField,
  StyledProductDetailsRight,
  StyledProductDetailsRightBottom,
  StyledProductDetailsRightTop,
  StyledProductDetailsWrapper,
  StyledHeading,
  StyledManufacturer,
} from './ProductDetails.styles';

import ProductDetailsLeftSection from '@organisms/ProductDetails/ProductDetailsLeftSection/ProductDetailsLeftSection';
import type { AxfoodOfflineProductViewModel, AxfoodProductDetailsViewModel } from '@occ/api-client';
import useProduct from '@organisms/ProductDetails/useProduct';
import PageLayout from '@organisms/PageLayout/PageLayout';
import trackProductDetail from '@helpers/analyticsHelpers/trackProductDetail';
import useCustomer from '@hooks/useCustomer';
import { useProductLabelSplash } from '@hooks/useProductLabelSplash';
import ProductDetailsSkeleton from '@organisms/ProductDetails/ProductDetailsSkeleton/ProductDetails.skeleton';

const errorHandler =
  (product: AxfoodProductDetailsViewModel | AxfoodOfflineProductViewModel | undefined) =>
  (error: Error, info: ErrorInfo) => {
    if (product) {
      logger.error({
        error: `Product Detail (${isOnlineProduct(product) ? product.code : product.name}) Error boundary caught error`,
        message: error.message,
        ...info,
      });
    }
  };

interface Props {
  isPage?: boolean;
}

const ProductDetails = ({ isPage = false }: Props) => {
  const searchParams = useSearchParams();
  const [quantity, setQuantity] = useState<number | null>(null);
  const t = useTranslations('product');
  const { product, isLoading, error } = useProduct();
  const { customer } = useCustomer();
  const isLoggedIn = !customer?.isAnonymous;

  const firstPromotion = product?.potentialPromotions && product.potentialPromotions[0];
  const productDetailsRef = useRef<HTMLDivElement | null>(null);

  const isMobile = useMediaQuery({
    query: Config.BREAKPOINTS.IS_MOBILE,
  });

  const [didScrollRelated, setDidScrollRelated] = useState(false);

  const closeModal = () => {
    if (typeof window !== 'undefined') {
      window.history.back();
    }
  };

  useEffect(() => {
    if (!isLoading && !error && product) {
      trackProductDetail(product, isLoggedIn);
    }
  }, [isLoading, error, product]);

  // If navigated with scrollTo=related, scroll to the first RelatedProducts beam when ready
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (didScrollRelated) return;
    if (isLoading || error) return;

    const shouldScrollToRelated = searchParams?.get('scrollTo') === 'related';
    if (!shouldScrollToRelated) return;

    let cancelled = false;
    const tryScroll = (attempt = 0) => {
      if (cancelled) return;
      const el = document.querySelector('[data-testid="relatedproducts-beam"]') as HTMLElement | null;
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setDidScrollRelated(true);
        return;
      }
      if (attempt < 15) {
        setTimeout(() => tryScroll(attempt + 1), 150);
      }
    };

    // small delay to ensure modal content has rendered
    const timer = window.setTimeout(() => tryScroll(0), 100);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [searchParams, isLoading, error, didScrollRelated]);

  let modalProps: Omit<ModalProps, 'children'> = {
    onClose: closeModal,
    size: 'large',
    hasCloseIcon: true,
    closeOnEscape: true,
  };

  if (error) {
    modalProps = {
      ...modalProps,
      id: 'product-not-found-modal',
      verticallyCentered: true,
      size: 'medium',
    };
  } else {
    modalProps = {
      ...modalProps,
      id: 'product-modal',
      isProductModal: true,
      noPadding: true,
    };
  }

  const productLabelSplash = useProductLabelSplash({
    product,
    blockElement: true,
  });

  const productContent = (
    <ErrorBoundary errorHandler={errorHandler(product)}>
      {isLoading && <ProductDetailsSkeleton isPage={isPage} />}
      {!isLoading && error && (
        <>
          <Heading variant="h2" size="small">
            {t('not_found.heading')}
          </Heading>
          <StyledNotFoundText>
            <Text type="body">{t('not_found.body')}</Text>
          </StyledNotFoundText>
          <Button centered onClick={closeModal}>
            {t('not_found.cta')}
          </Button>
        </>
      )}
      {!isLoading && product && (
        <StyledProductDetailsWrapper ref={productDetailsRef} itemScope itemType="https://schema.org/Product">
          {isOnlineProduct(product) && <meta itemProp="description" content={product.description} />}
          {!isOfflineProduct(product) && product?.breadCrumbs?.length > 0 && isPage && (
            <BreadcrumbsWrapper role="navigation" aria-label={t('breadcrumbPageLabel')} $isPage>
              <Breadcrumbs breadcrumbs={product.breadCrumbs.slice(1, product.breadCrumbs.length - 1)} />
            </BreadcrumbsWrapper>
          )}
          <StyledProductDetails>
            <ProductDetailsLeftSection product={product} quantity={quantity} isPage={isPage} />
            <StyledProductDetailsRight $isPage={isPage}>
              <StyledProductDetailsRightTop>
                {!isOfflineProduct(product) && product?.breadCrumbs?.length > 0 && !isPage && (
                  <BreadcrumbsWrapper role="navigation" aria-label={t('breadcrumbModalLabel')}>
                    <Breadcrumbs breadcrumbs={product.breadCrumbs.slice(1, product.breadCrumbs.length - 1)} />
                  </BreadcrumbsWrapper>
                )}
                <StyledHeading variant="h2">
                  <span itemProp="name">{product?.name}</span>
                </StyledHeading>

                {productLabelSplash}

                <StyledProductDetailsManufacturerWrapper>
                  {isOfflineProduct(product) && product.manufacturer && (
                    <StyledManufacturer type="body" size="small" element="span">
                      <span itemProp="brand">{product?.manufacturer}</span>
                    </StyledManufacturer>
                  )}

                  <StyledManufacturer type="subhead" size="small" element="span">
                    {isOnlineProduct(product) && product.manufacturer && (
                      <Link
                        href={{
                          pathname: '/sok',
                          query: { q: `${product.manufacturer}:topRated:commercialName2:${product.manufacturer}` },
                        }}
                        prefetch={false}
                      >
                        <span itemProp="brand">{product.manufacturer}</span>
                      </Link>
                    )}
                  </StyledManufacturer>
                  {isOnlineProduct(product) && product.fruitAndVegetableVariety && product.productLine2 && (
                    <Text type="body" size="small" element="span">
                      {`${product.fruitAndVegetableVariety}, ${product.productLine2}`}
                    </Text>
                  )}
                  {(isOfflineProduct(product) || !product.fruitAndVegetableVariety) && (
                    <Text type="body" size="small" element="span" color="darkerGray">
                      {product.displayVolume && product.displayVolume}
                      {isOnlineProduct(product) &&
                        product.originCountry &&
                        `${product.displayVolume ? ',' : ''} ${product.originCountry}`}
                    </Text>
                  )}
                </StyledProductDetailsManufacturerWrapper>

                {isOfflineProduct(product) &&
                  firstPromotion &&
                  isOfflinePromotion(firstPromotion) &&
                  firstPromotion.description && <Text type="body">{firstPromotion.description}</Text>}

                <StyledProductDetailsPriceAndPromotion>
                  <ProductDetailsPriceAndPromotionDetails product={product} />
                </StyledProductDetailsPriceAndPromotion>

                {isOfflineProduct(product) && product?.energyDeclaration && (
                  <StyledProductDetailsEnergyDeclaration data-testid="energyDeclaration">
                    <Heading variant="h3" label>
                      {t('energyDeclaration.heading')}
                    </Heading>

                    {product.energyDeclaration.link && (
                      <Text type="body">
                        <Link
                          href={product.energyDeclaration.link}
                          data-testid="energyDeclaration_link"
                          target="_blank"
                        >
                          {t('energyDeclaration.linkText')}
                        </Link>
                      </Text>
                    )}

                    {product.energyDeclaration.image && (
                      <StyledEnergyDeclarationImage
                        alt={t('energyDeclaration.heading')}
                        src={product.energyDeclaration.image}
                        data-testid="energyDeclaration_image"
                      />
                    )}
                  </StyledProductDetailsEnergyDeclaration>
                )}

                {isOnlineProduct(product) && (
                  <>
                    <StyledProductDetailsQuantityField>
                      <ProductQuantityControl product={product} variant="product" quantityCallback={setQuantity} />
                    </StyledProductDetailsQuantityField>
                    {!isMobile && <ProductDetailsTabs data={product} />}
                  </>
                )}
              </StyledProductDetailsRightTop>

              {isOnlineProduct(product) && (
                <StyledProductDetailsRightBottom>
                  {isMobile && <ProductDetailsAccordion data={product as AxfoodProductDetailsViewModel} />}
                </StyledProductDetailsRightBottom>
              )}
            </StyledProductDetailsRight>
          </StyledProductDetails>
          {isOnlineProduct(product) && <ProductDetailsRelated product={product} isPage={isPage} />}
          <>
            <div itemProp="offers" itemType="http://schema.org/Offer" itemScope>
              <meta
                itemProp="availability"
                content={
                  isOnlineProduct(product) && product.outOfStock
                    ? 'https://schema.org/OutOfStock'
                    : 'https://schema.org/InStock'
                }
              />
              <meta itemProp="priceCurrency" content="SEK" />
              <meta itemProp="price" content={product.priceNoUnit} />
            </div>
          </>
        </StyledProductDetailsWrapper>
      )}
    </ErrorBoundary>
  );

  const productWrapper = () => {
    if (isPage) {
      return <PageLayout noMobilePadding>{productContent}</PageLayout>;
    }
    return <Modal {...modalProps}>{productContent}</Modal>;
  };

  useEffect(() => {
    if (!isLoading) {
      const firstButton = productDetailsRef.current?.querySelector('button');
      firstButton?.focus();
    }
  }, [isLoading]);

  return productWrapper();
};

export default ProductDetails;
